import { trackCategoriesResultsScreenView } from "@faire/web-api/events/categories/view/resultsScreen";
import { trackSignupModalOpenCatnavBottomCtaClick } from "@faire/web-api/events/signup/click/modalOpenCatnavBottomCta";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { getLocationOrThrow } from "@faire/web/common/globals/getLocation";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useMergedRefs } from "@faire/web/ui/hooks/useMergedRefs";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { observer } from "mobx-react";
import React, { Suspense, useEffect } from "react";
import styled from "styled-components";

import {
  recordLandingActivity,
  recordSignupActivity,
} from "@faire/retailer/actions/user/recordLoggedOutActivity";
import { FilterInfoSticky } from "@faire/retailer/components/Filters/FilterInfoSticky";
import { FilterName } from "@faire/retailer/components/Filters/FilterName";
import { useFilterStore } from "@faire/retailer/components/Filters/useFilterStore";
import { Footer } from "@faire/retailer/components/Footer";
import { KeepExploringCarousel } from "@faire/retailer/components/KeepExploringCarousel";
import { LazyLoggedOutMobileSignUpSnackbar } from "@faire/retailer/components/LoggedOutMobileSignUpSnackbar";
import { NoResultsContainer } from "@faire/retailer/components/NoResults";
import { SignUpBanner } from "@faire/retailer/components/SignUpBanner/SignUpBanner";
import { PaddedContainer } from "@faire/retailer/features/Marketplace/style";
import {
  popSignUp,
  SignUpElement,
  SignUpType,
} from "@faire/retailer/lib/popSignUp";
import { assignLargerPageSizeOnLoggedOutCategory } from "@faire/retailer/settings/getSettingEnableLargerPageSizeLoggedOutCategory";
import { assignEnableLoggedOutProductTileRedesign } from "@faire/retailer/settings/getSettingEnableLoggedOutProductTileRedesign";
import { useCategoryPageScroll } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageScroll";
import { LoggedOutCategoryPageBrandReviewsCarousel } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageBrandReviewsCarousel";
import { LoggedOutCategoryPageFAQs } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageFAQs/LoggedOutCategoryPageFAQs";
import { LoggedOutCategoryPageMoreInC3Pills } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageMoreInC3Pills/LoggedOutCategoryPageMoreInC3Pills";
import { LoggedOutCategoryPageRelatedSearches } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageRelatedSearches";
import { LoggedOutCategoryPageSeoTextBlock } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageBelowResults/LoggedOutCategoryPageSeoTextBlock";
import { LoggedOutCategoryPageFilterSidebar } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPageFilterSidebar";
import { LoggedOutCategoryPageHeaderAndCarousel } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPageHeaderAndCarousel";
import { LoggedOutCategoryPageHelmetAndBreadcrumb } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPageHelmetAndBreadcrumb";
import { LoggedOutCategoryPagePagination } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPagePagination";
import { LoggedOutCategoryPageProductList } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageFilterAndResults/LoggedOutCategoryPageProductList";
import { LoggedOutCategoryPageLayoutElements } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageLayoutElements/LoggedOutCategoryPageLayoutElements";
import { VisibilityTracker } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/VisibilityTracker";
import { useLoggedOutCategoryPageViewStateContext } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/LoggedOutCategoryPageViewState/useLoggedOutCategoryPageViewState";
import { useLoggedOutCategoryPageCurrentCategory } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentCategory";
import { useLoggedOutCategoryPageCurrentNavigationPath } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentNavigationPath";
import { useLoggedOutCategoryPageisLoadedAndHasNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/hooks/useLoggedOutCategoryPageisLoadedAndHasNoResults";

const InternalLoggedOutCategoryPageBase: React.FC<{
  categoryPageLoadTimingRef?: React.Ref<HTMLDivElement>;
}> = ({ categoryPageLoadTimingRef }) => {
  const { isTabletAndBelow } = useViewport();
  const { loggedOutBannersViewState } =
    useLoggedOutCategoryPageViewStateContext();

  const isLoadedAndHasNoResults =
    useLoggedOutCategoryPageisLoadedAndHasNoResults();

  const categoryModel = useLoggedOutCategoryPageCurrentCategory();
  const navigationPath = useLoggedOutCategoryPageCurrentNavigationPath();

  const { scrollToTop, scrollRef } = useCategoryPageScroll();
  const mergedRef = useMergedRefs(scrollRef, categoryPageLoadTimingRef);

  useEffect(() => {
    assignEnableLoggedOutProductTileRedesign();
  }, []);

  useEffect(() => {
    assignLargerPageSizeOnLoggedOutCategory();
  }, []);

  const renderList = () => {
    return (
      <LoggedOutCategoryPageProductList
        bannersViewState={loggedOutBannersViewState}
      />
    );
  };

  const {
    clearAllFilters,
    data: { activeFilterCount },
  } = useFilterStore(["activeFilterCount"]);

  const handleSignUp = () =>
    popSignUp(SignUpType.GENERIC, trackSignupModalOpenCatnavBottomCtaClick, {
      element: SignUpElement.BELOW_FOLD,
      sourceId: "logged_out_category_page_signup_banner",
    });

  useEffect(() => {
    recordLandingActivity({
      category_path_identifier: categoryModel?.mostSpecificPathIdentifier,
    });

    try {
      recordSignupActivity(
        {
          category_path_identifier: categoryModel?.mostSpecificPathIdentifier,
        },
        getLocationOrThrow().pathname
      );
    } catch (e) {
      // If there's no window.location don't record
    }
  }, [categoryModel?.mostSpecificPathIdentifier]);

  useEffect(() => {
    trackCategoriesResultsScreenView(navigationPath);
  }, [navigationPath]);

  if (!categoryModel) {
    return null;
  }

  return (
    <>
      <Column>
        <LoggedOutCategoryPageHelmetAndBreadcrumb />
        <PaddedRow>
          <LoggedOutCategoryPageFilterSidebar />
          <Column style={{ width: "100%" }}>
            <LoggedOutCategoryPageHeaderAndCarousel />
            <Spacer height="3x" />
            {isLoadedAndHasNoResults ? (
              <NoResultsContainer
                showProductFirstResults
                hasFiltersApplied={activeFilterCount > 0}
                onClearFilters={clearAllFilters}
                divRef={mergedRef}
              />
            ) : (
              <>
                {isTabletAndBelow ? (
                  <FilterInfoSticky name={FilterName.LOGGED_OUT_CATEGORY} />
                ) : null}
                <div ref={mergedRef} style={{ paddingTop: getSpacing("1x") }}>
                  {renderList()}
                </div>
              </>
            )}
          </Column>
        </PaddedRow>

        <LoggedOutCategoryPagePagination scrollToTop={scrollToTop} />
        <PaddedContainer $noPaddingOnMobile>
          <Spacer size="5x" />
          <WhenVisibilityIsImminent>
            <LoggedOutCategoryPageLayoutElements
              placement={[ILayoutElement.Placement.BOTTOM]}
            />
            <LoggedOutCategoryPageMoreInC3Pills />
          </WhenVisibilityIsImminent>
          <LoggedOutCategoryPageRelatedSearches />
          <LoggedOutCategoryPageBrandReviewsCarousel />
          <VisibilityTracker name="keep-exploring-carousel">
            <KeepExploringCarousel
              name="logged-out-category-keep-exploring"
              clickTrackPath="logged-out-category/keep-exploring-carousel"
              removeCategoryPath={categoryModel.pathIdentifier}
              onClick={() => {
                getWindowOrThrow().scrollTo?.(0, 0);
              }}
            />
          </VisibilityTracker>

          <LoggedOutCategoryPageFAQs />
        </PaddedContainer>
        <VisibilityTracker name="signup-banner">
          <SignUpBanner onClick={handleSignUp} />
        </VisibilityTracker>
        <LoggedOutCategoryPageSeoTextBlock />
        <Footer />
      </Column>

      <Suspense fallback={null}>
        <LazyLoggedOutMobileSignUpSnackbar />
      </Suspense>
    </>
  );
};

export const LoggedOutCategoryPageBase = observer(
  InternalLoggedOutCategoryPageBase
);

const PaddedRow = styled(Row)`
  height: 100%;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, "5x")};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing(0, "2x")};
  }
`;
